import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { KbArticle, KbCategory, KbSearchResult } from '@evidentid/tprm-portal-lib/models/kb';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

export interface KbRequirement {
    gatewayClient: GatewayApiClient;
}

export interface KbState {
    tree: KbCategory[];
    treeStatus: OperationStatus;
    articles: Record<string, KbArticle>;
    articleStatus: Record<string, OperationStatus>;
    openArticleId: string | null;
    searchQuery: string;
    searchResults: KbSearchResult[];
    searchStatus: OperationStatus;
}

// Object URLs (or the promise for one still being fetched) per image, and how many views currently
// show each article, kept outside reactive state: they are handles to browser-held blobs, not data
// to render from. Keyed by the app the store belongs to, so separate apps never share them.
interface ImageUrlCache {
    urls: Map<string, Promise<string | null>>;
    holders: Map<string, number>;
}

const imageUrlCaches = new WeakMap<object, ImageUrlCache>();

function imageUrlCacheOf(store: { $app: object }): ImageUrlCache {
    let cache = imageUrlCaches.get(store.$app);
    if (!cache) {
        cache = { urls: new Map(), holders: new Map() };
        imageUrlCaches.set(store.$app, cache);
    }
    return cache;
}

// Revokes each URL once its fetch settles, so a load still in flight is revoked on arrival.
function revokeImageUrls(urls: Promise<string | null>[]): void {
    urls.forEach((url) => url.then((objectUrl) => objectUrl && URL.revokeObjectURL(objectUrl)));
}

function appOf(store: { $app: any }): KbRequirement {
    return store.$app;
}

const state = (): KbState => ({
    tree: [],
    treeStatus: OperationStatus.uninitialized,
    articles: {},
    articleStatus: {},
    openArticleId: null,
    searchQuery: '',
    searchResults: [],
    searchStatus: OperationStatus.uninitialized,
});

export const useKbStore = defineStore('kb', {
    state,
    getters: {
        // articleStatus/articles are plain objects keyed by a route param, so a
        // lookup by key alone would also match an inherited Object.prototype
        // member (e.g. id "toString"). Restricting the read to the object's own
        // properties keeps an unvalidated id from resolving to that member.
        articleStatusFor: (state) => (id: string): OperationStatus | undefined => (
            Object.prototype.hasOwnProperty.call(state.articleStatus, id) ? state.articleStatus[id] : undefined
        ),
        articleFor: (state) => (id: string): KbArticle | undefined => (
            Object.prototype.hasOwnProperty.call(state.articles, id) ? state.articles[id] : undefined
        ),
    },
    actions: {
        async loadTree(payload: { force?: boolean } = {}) {
            // Only a load already in flight or already successful is skipped. A
            // mount that finds the tree in an error state retries instead of
            // reusing the stale failure, so one transient failure does not
            // disable help for the rest of the session; the mount that produced
            // the error still renders it, since nothing else touches treeStatus
            // during that same mount.
            const attemptInProgressOrDone = [ OperationStatus.loading, OperationStatus.success ]
                .includes(this.treeStatus);
            if (!payload.force && attemptInProgressOrDone) {
                return this.treeStatus === OperationStatus.success;
            }
            try {
                this.treeStatus = OperationStatus.loading;
                this.tree = await appOf(this).gatewayClient.tprmUi().getKbTree();
                this.treeStatus = OperationStatus.success;
                return true;
            } catch (error) {
                console.warn('load kb tree error', error);
                this.treeStatus = OperationStatus.error;
                return false;
            }
        },
        async loadArticle(id: string, payload: { force?: boolean } = {}) {
            // Skipped only while a load is in flight or has already succeeded, matching
            // `loadTree`: an article left in an error state is retried on the next mount
            // rather than held for the rest of the session. The getter is an own-property
            // lookup, so an id like `toString` cannot match an inherited member and skip
            // the fetch.
            const status = this.articleStatusFor(id);
            const attemptInProgressOrDone = status === OperationStatus.loading || status === OperationStatus.success;
            if (!payload.force && attemptInProgressOrDone) {
                return status === OperationStatus.success;
            }
            try {
                this.articleStatus = { ...this.articleStatus, [id]: OperationStatus.loading };
                const article = await appOf(this).gatewayClient.tprmUi().getKbArticle(id);
                this.articles = { ...this.articles, [id]: article };
                this.articleStatus = { ...this.articleStatus, [id]: OperationStatus.success };
                return true;
            } catch (error) {
                console.warn('load kb article error', error);
                this.articleStatus = { ...this.articleStatus, [id]: OperationStatus.error };
                return false;
            }
        },
        // Fetches an article image with the reader's credentials (an <img src> request carries
        // none) and returns an object URL for it. Concurrent and repeated calls for one image
        // share a single request; a failure resolves null and is dropped from the cache so a
        // later call retries.
        loadArticleImage(articleId: string, attachmentId: string): Promise<string | null> {
            const { urls } = imageUrlCacheOf(this);
            const key = `${articleId}/${attachmentId}`;
            const cached = urls.get(key);
            if (cached) {
                return cached;
            }
            const pending: Promise<string | null> = appOf(this).gatewayClient.tprmUi()
                .getKbArticleImage(articleId, attachmentId)
                .then((blob) => URL.createObjectURL(blob))
                .catch((error): null => {
                    console.warn('load kb article image error', error);
                    // The entry may already have been evicted and replaced by a newer request.
                    if (urls.get(key) === pending) {
                        urls.delete(key);
                    }
                    return null;
                });
            urls.set(key, pending);
            return pending;
        },
        // A view showing an article's images registers here; the object URLs stay alive while any
        // view holds the article, so one shown in both the page and the panel is not broken by
        // either closing.
        acquireArticleImages(articleId: string) {
            const { holders } = imageUrlCacheOf(this);
            holders.set(articleId, (holders.get(articleId) || 0) + 1);
        },
        // Drops one hold; the last release revokes and evicts every URL cached for the article.
        releaseArticleImages(articleId: string) {
            const { urls, holders } = imageUrlCacheOf(this);
            const remaining = (holders.get(articleId) || 0) - 1;
            if (remaining > 0) {
                holders.set(articleId, remaining);
                return;
            }
            holders.delete(articleId);
            const prefix = `${articleId}/`;
            const evicted: Promise<string | null>[] = [];
            urls.forEach((url, key) => {
                if (key.startsWith(prefix)) {
                    evicted.push(url);
                    urls.delete(key);
                }
            });
            revokeImageUrls(evicted);
        },
        async search(query: string) {
            this.searchQuery = query;
            const trimmedQuery = query.trim();
            if (!trimmedQuery) {
                this.searchResults = [];
                this.searchStatus = OperationStatus.uninitialized;
                return true;
            }
            try {
                this.searchStatus = OperationStatus.loading;
                const results = await appOf(this).gatewayClient.tprmUi().searchKb(trimmedQuery);
                // A slower response for an earlier query must not overwrite the results
                // of a query typed after it. Comparing trimmed values means padding
                // added or removed after the request went out doesn't count as a
                // different query.
                if (this.searchQuery.trim() !== trimmedQuery) {
                    return true;
                }
                this.searchResults = results;
                this.searchStatus = OperationStatus.success;
                return true;
            } catch (error) {
                console.warn('kb search error', error);
                if (this.searchQuery.trim() === trimmedQuery) {
                    this.searchStatus = OperationStatus.error;
                }
                return false;
            }
        },
        // Updates the query text immediately, independent of when the debounced
        // search actually runs, so the input the user sees is never behind what
        // they typed and survives a remount alongside the results it produced.
        setQuery(query: string) {
            this.searchQuery = query;
        },
        // Hides the results panel without touching the query text, so a selection,
        // Escape, or a click outside closes the dropdown but leaves what the user
        // typed in place.
        clearResults() {
            this.searchResults = [];
            this.searchStatus = OperationStatus.uninitialized;
        },
        openPanel(id: string) {
            this.openArticleId = id;
        },
        closePanel() {
            this.openArticleId = null;
        },
        reset() {
            const cache = imageUrlCacheOf(this);
            revokeImageUrls([ ...cache.urls.values() ]);
            cache.urls.clear();
            cache.holders.clear();
            this.$reset();
        },
    },
});
